import React from "react";
import moment from "moment";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import {
  Email as EmailIcon,
  Task as TaskIcon,
  PauseCircle,
  PlayCircle,
  StopCircle,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { useSelector } from "react-redux";

import { StandadCard } from "../../../components/Cards";
import { ConfirmDialog } from "../../../components/Dialogs/ConfirmDialog";
import { LoadState, ROLES } from "../../../constants/enums";
import { useRoles } from "../../../security/RolesProvider/RolesProvider";
import { useDispatchWrapper } from "../../../hooks";
import { IStoreState } from "../../../redux/initialStoreState";
import {
  changeEnrolmentStatusAsync,
  fetchRecordSequencesAsync,
} from "../../../redux/emailSequences/emailSequencesActions";
import {
  ISequenceEnrolment,
  ISequenceEnrolmentStep,
} from "../../../redux/emailSequences/emailSequences.types";
import {
  resolveSkipReasonLabel,
  SequenceModule,
} from "../emailSequences.labels";
import { EnrolmentStatusChip, StepResultChip } from "./SequenceChips";
import { AddToSequenceDialog } from "./AddToSequenceDialog";

interface IProps {
  module: SequenceModule;
  moduleCode: string | number;
}

const TimelineRow: React.FC<{
  step: ISequenceEnrolmentStep;
  isAdmin: boolean;
}> = ({ step, isAdmin }) => {
  const [showError, setShowError] = React.useState(false);
  const title =
    step.step_name || step.template_code || step.task_name || "Step";

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        {step.action_type === "EMAIL" ? (
          <EmailIcon fontSize="small" color="action" />
        ) : (
          <TaskIcon fontSize="small" color="action" />
        )}
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        <StepResultChip result={step.result} />
        {step.result === "SKIPPED" && step.skip_reason && (
          <Chip size="small" label={resolveSkipReasonLabel(step.skip_reason)} />
        )}
        {step.executed_at && (
          <Typography variant="caption" color="text.secondary">
            {moment(step.executed_at).format("MMM D, YYYY h:mm A")}
          </Typography>
        )}
      </Stack>

      {/* Recipients (UI PRD R2 §4) */}
      {step.action_type === "EMAIL" &&
        ((step.to_emails && step.to_emails.length > 0) ||
          (step.cc_emails && step.cc_emails.length > 0)) && (
          <Box sx={{ pl: 3, mt: 0.25 }}>
            {step.to_emails && step.to_emails.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", wordBreak: "break-word" }}
              >
                To: {step.to_emails.join(", ")}
              </Typography>
            )}
            {step.cc_emails && step.cc_emails.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", wordBreak: "break-word" }}
              >
                CC: {step.cc_emails.join(", ")}
              </Typography>
            )}
          </Box>
        )}

      {/* Email delivery badges */}
      {step.action_type === "EMAIL" && step.result === "SENT" && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ pl: 3, mt: 0.5 }}>
          {step.delivery_status && (
            <Chip size="small" variant="outlined" label={step.delivery_status} />
          )}
          {step.opened && (
            <Chip size="small" color="success" label="Opened" />
          )}
          {step.clicked && <Chip size="small" color="info" label="Clicked" />}
          {step.email_history_code && (
            <Link
              component="button"
              variant="caption"
              onClick={() =>
                window.open(
                  `/email-history/${step.email_history_code}`,
                  "_blank"
                )
              }
            >
              View email
            </Link>
          )}
        </Stack>
      )}

      {/* Task link */}
      {step.action_type === "TASK" && step.task_code && (
        <Box sx={{ pl: 3, mt: 0.5 }}>
          <Link
            component="button"
            variant="caption"
            onClick={() => window.open(`/task/${step.task_code}`, "_blank")}
          >
            View task {step.task_code}
          </Link>
        </Box>
      )}

      {/* Failure log (admins only) */}
      {step.result === "FAILED" && step.error_log && isAdmin && (
        <Box sx={{ pl: 3, mt: 0.5 }}>
          <Button size="small" onClick={() => setShowError((s) => !s)}>
            {showError ? "Hide error" : "Show error"}
          </Button>
          <Collapse in={showError}>
            <Box
              sx={{
                bgcolor: "#fafafa",
                p: 1,
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre-wrap",
              }}
            >
              {step.error_log}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

const EnrolmentCard: React.FC<{
  enrolment: ISequenceEnrolment;
  isAdmin: boolean;
  onAction: (
    e: ISequenceEnrolment,
    type: "pause" | "resume" | "stop"
  ) => void;
  onRequestStop: (e: ISequenceEnrolment) => void;
}> = ({ enrolment, isAdmin, onAction, onRequestStop }) => {
  const [expanded, setExpanded] = React.useState(true);
  const steps = enrolment.steps || [];
  const nextStep = steps.find((s) => !s.executed_at);

  return (
    <StandadCard sx={{ mb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6">
            {enrolment.sequence_name || enrolment.sequence_code}
          </Typography>
          <EnrolmentStatusChip status={enrolment.status} />
          <Typography variant="body2" color="text.secondary">
            Step {enrolment.current_step_order}
            {enrolment.total_steps ? ` of ${enrolment.total_steps}` : ""}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          {enrolment.status === "ACTIVE" && (
            <Button
              size="small"
              startIcon={<PauseCircle />}
              onClick={() => onAction(enrolment, "pause")}
            >
              Pause
            </Button>
          )}
          {enrolment.status === "PAUSED" && (
            <Button
              size="small"
              startIcon={<PlayCircle />}
              onClick={() => onAction(enrolment, "resume")}
            >
              Resume
            </Button>
          )}
          {(enrolment.status === "ACTIVE" ||
            enrolment.status === "PAUSED") && (
            <Button
              size="small"
              color="error"
              startIcon={<StopCircle />}
              onClick={() => onRequestStop(enrolment)}
            >
              Stop
            </Button>
          )}
        </Stack>
      </Stack>

      {nextStep && enrolment.next_run_at && (
        <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
          Next: {nextStep.action_type === "EMAIL" ? "Email" : "Task"} '
          {nextStep.step_name ||
            nextStep.template_code ||
            nextStep.task_name}
          ' · {moment(enrolment.next_run_at).format("ddd HH:mm")}
        </Typography>
      )}

      <Button
        size="small"
        endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
        onClick={() => setExpanded((e) => !e)}
        sx={{ mt: 1 }}
      >
        Timeline
      </Button>
      <Collapse in={expanded}>
        <Divider sx={{ my: 1 }} />
        {steps.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No steps run yet.
          </Typography>
        ) : (
          steps.map((step, i) => (
            <TimelineRow key={i} step={step} isAdmin={isAdmin} />
          ))
        )}
      </Collapse>
    </StandadCard>
  );
};

export const RecordSequencesPanel: React.FC<IProps> = ({
  module,
  moduleCode,
}) => {
  const dispatch = useDispatchWrapper();
  const { roleName } = useRoles();
  const isAdmin = roleName === ROLES.ADMIN;

  const { recordSequences, recordSequencesLoading } = useSelector(
    (s: IStoreState) => s.emailSequences
  );
  const [stopConfirm, setStopConfirm] =
    React.useState<ISequenceEnrolment | null>(null);
  const [openAdd, setOpenAdd] = React.useState(false);

  const fetchPanel = () => {
    dispatch(fetchRecordSequencesAsync(module, moduleCode));
  };

  React.useEffect(() => {
    fetchPanel();
     
  }, [module, moduleCode]);

  const handleAction = (
    enrolment: ISequenceEnrolment,
    type: "pause" | "resume" | "stop"
  ) => {
    dispatch(
      changeEnrolmentStatusAsync(enrolment.enrollment_code, type, (ok) => {
        if (ok) {
          setStopConfirm(null);
          fetchPanel();
        }
      })
    );
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h4">Sequences</Typography>
        <Button variant="contained" onClick={() => setOpenAdd(true)}>
          Add to sequence
        </Button>
      </Stack>

      {recordSequencesLoading === LoadState.InProgress ? (
        <Typography>Loading…</Typography>
      ) : recordSequences.length === 0 ? (
        <StandadCard sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This record isn't in any sequence.
          </Typography>
          <Button variant="outlined" onClick={() => setOpenAdd(true)}>
            Add to sequence
          </Button>
        </StandadCard>
      ) : (
        recordSequences.map((enrolment) => (
          <EnrolmentCard
            key={enrolment.enrollment_code}
            enrolment={enrolment}
            isAdmin={isAdmin}
            onAction={handleAction}
            onRequestStop={(e) => setStopConfirm(e)}
          />
        ))
      )}

      {stopConfirm && (
        <ConfirmDialog
          open={Boolean(stopConfirm)}
          title="Stop sequence"
          content="This record will get no more steps from this sequence. It can't be restarted for this cycle."
          onClose={() => setStopConfirm(null)}
          onConfrim={() => handleAction(stopConfirm, "stop")}
        />
      )}

      <AddToSequenceDialog
        open={openAdd}
        module={module}
        moduleCodes={[moduleCode]}
        onClose={() => setOpenAdd(false)}
        onEnrolled={fetchPanel}
      />
    </Box>
  );
};

/**
 * Small badge for the record header: shows "In sequence" when any enrolment
 * is ACTIVE or PAUSED (PRD §5.5). Reads from the already-fetched slice; call
 * fetchRecordSequencesAsync somewhere in the record screen first, or render
 * this inside a screen that mounts RecordSequencesPanel.
 */
export const InSequenceBadge: React.FC = () => {
  const { recordSequences } = useSelector(
    (s: IStoreState) => s.emailSequences
  );
  const inSequence = recordSequences.some(
    (e) => e.status === "ACTIVE" || e.status === "PAUSED"
  );
  if (!inSequence) return null;
  return <Chip size="small" color="primary" label="In sequence" />;
};
